/**
 * DemographicOverlay — Census block group choropleth rendered inside TurfMap.
 *
 * Must be a child of react-map-gl <Map> so it can use useMap() and
 * render <Source>/<Layer> in the correct map context.
 *
 * Data flow:
 * 1. Listen for map moveend events (debounced)
 * 2. Fetch block group boundaries from TIGERweb for current viewport
 * 3. Fetch ACS demographics per county (cached across pans)
 * 4. Join demographics onto GeoJSON features
 * 5. Render as fill + outline layers
 */

import { useCallback, useRef } from 'react';
import { Source, Layer } from 'react-map-gl/mapbox';
import {
  fetchBlockGroupBoundaries,
  fetchCountyDemographics,
  type BlockGroupDemographics,
} from '@/services/censusApi';
import { useViewportGeoJSON, type ViewportFetchArgs } from '@/hooks/useViewportGeoJSON';

export type DemographicMetric =
  | 'medianIncome'
  | 'renterRate'
  | 'collegeRate'
  | 'pctBlack'
  | 'pctHispanic';

interface DemographicOverlayProps {
  visible: boolean;
  metric: DemographicMetric;
  opacity?: number;
}

/** Minimum zoom to fetch demographics (roughly county level). */
const MIN_ZOOM = 9;
/** Debounce delay for map move events. */
const DEBOUNCE_MS = 600;

// ---------------------------------------------------------------------------
// Mapbox GL color expressions per metric
// ---------------------------------------------------------------------------

// Per-metric color ramps — deliberately avoids blue (voter dots are blue)
const COLOR_SCALES: Record<DemographicMetric, string[]> = {
  medianIncome: [], // handled separately (red→green with null guard)
  renterRate:   ['#fcfbfd', '#d2d0e7', '#9e9ac8', '#756bb1', '#4a1486'], // purples
  collegeRate:  ['#feedde', '#fdbe85', '#fd8d3c', '#e6550d', '#a63603'], // oranges
  pctBlack:     ['#edf8e9', '#bae4b3', '#74c476', '#31a354', '#006d2c'], // greens
  pctHispanic:  ['#fff5eb', '#fdd0a2', '#f16913', '#d94801', '#8c2d04'], // warm ambers
};

function buildFillColor(metric: DemographicMetric) {
  if (metric === 'medianIncome') {
    // Red (low) → Yellow (mid) → Green (high)
    return [
      'case',
      ['any', ['!', ['has', 'medianIncome']], ['==', ['get', 'medianIncome'], null]],
      '#e0e0e0',
      [
        'interpolate',
        ['linear'],
        ['get', 'medianIncome'],
        20000, '#d73027',
        35000, '#fc8d59',
        50000, '#fee08b',
        70000, '#d9ef8b',
        100000, '#1a9850',
      ],
    ];
  }

  const scale = COLOR_SCALES[metric];
  return [
    'interpolate',
    ['linear'],
    ['coalesce', ['get', metric], 0],
    0,    scale[0],
    0.15, scale[1],
    0.3,  scale[2],
    0.5,  scale[3],
    0.75, scale[4],
  ];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DemographicOverlay({
  visible,
  metric,
  opacity = 0.45,
}: DemographicOverlayProps) {
  // County-level demographics cache (persists across pans, never refetched)
  const countyCache = useRef(new Map<string, Map<string, BlockGroupDemographics>>());
  const censusKey = import.meta.env.VITE_CENSUS_API_KEY as string | undefined;

  const fetchForViewport = useCallback(
    async ({ bounds }: ViewportFetchArgs): Promise<GeoJSON.FeatureCollection | null> => {
      // 1. Block group boundaries from TIGERweb
      const boundaries = await fetchBlockGroupBoundaries(bounds);
      if (!boundaries.features?.length) return null;

      // 2. Determine which counties we still need to fetch
      const neededCounties = new Set<string>();
      for (const f of boundaries.features) {
        const { STATE, COUNTY } = f.properties as Record<string, string>;
        const key = `${STATE}|${COUNTY}`;
        if (!countyCache.current.has(key)) {
          neededCounties.add(key);
        }
      }

      // 3. Fetch demographics for uncached counties (parallel)
      if (neededCounties.size > 0) {
        await Promise.all(
          Array.from(neededCounties).map(async (key) => {
            const [state, county] = key.split('|');
            try {
              const data = await fetchCountyDemographics(state, county, censusKey);
              countyCache.current.set(key, data);
            } catch (err) {
              console.warn(`Failed to fetch demographics for ${key}:`, err);
              // Set empty map so we don't retry on every pan
              countyCache.current.set(key, new Map());
            }
          }),
        );
      }

      // 4. Enrich features with demographic properties
      return {
        type: 'FeatureCollection',
        features: boundaries.features.map((feature) => {
          const props = feature.properties as Record<string, string>;
          const demo = countyCache.current.get(`${props.STATE}|${props.COUNTY}`)?.get(props.GEOID);

          return {
            ...feature,
            properties: {
              ...feature.properties,
              medianIncome: demo?.medianIncome ?? null,
              renterRate: demo?.renterRate ?? 0,
              collegeRate: demo?.collegeRate ?? 0,
              pctWhite: demo?.pctWhite ?? 0,
              pctBlack: demo?.pctBlack ?? 0,
              pctHispanic: demo?.pctHispanic ?? 0,
              hasData: !!demo,
            },
          };
        }),
      };
    },
    [censusKey],
  );

  const { data: geojson } = useViewportGeoJSON(visible, fetchForViewport, {
    minZoom: MIN_ZOOM,
    debounceMs: DEBOUNCE_MS,
  });

  // ---- Render ----

  if (!geojson) return null;

  return (
    <Source id="demographics" type="geojson" data={geojson}>
      {/* Block group fill — colored by metric */}
      <Layer
        id="demographics-fill"
        type="fill"
        paint={{
          'fill-color': buildFillColor(metric) as never,
          'fill-opacity': [
            'case',
            ['==', ['get', 'hasData'], true],
            opacity,
            0.05,
          ] as never,
        }}
      />

      {/* Block group outlines */}
      <Layer
        id="demographics-line"
        type="line"
        paint={{
          'line-color': '#ffffff',
          'line-width': 0.5,
          'line-opacity': [
            'case',
            ['==', ['get', 'hasData'], true],
            0.4,
            0,
          ] as never,
        }}
      />
    </Source>
  );
}
